import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import { SETTLE, MORPH, settleDelay } from '../lib/motion';
import { AccentInjector } from '../components/AccentInjector';
import { useEffectiveMe } from '../lib/viewAs';
import {
  resolveGateIdentity,
  previewIdentity,
  type GateIdentity,
  type GatePersona,
  type LiveMatchRef,
} from './gate/gateIdentity';
import { gateDoors, gateHeader, type GateScreenId } from './gate/gateDoors';
import { GatePill } from './gate/ui';

// ─────────────────────────────────────────────────────────────────────────────
// GATE — the role×time-aware entry. /ggportal lands here (via Handshake's
// default returnTo). The GATE only ROUTES; the destinations below are existing
// screens that do the real work. One decision per screen, ultra-minimal.
//
// Look/feel locked to Lisa's master mockups: grey "lobby" ground, white pills,
// one accent pill = the contextual primary action, GG Anvil lockup, a quiet
// BACK. Motion = the shared "quiet & quick" layer (lib/motion.ts): items settle
// in on load (staggered, eases to rest), pills morph slightly on hover/press.
// No glow, no border. Tapestry 12-portal-design-overhaul.
// ─────────────────────────────────────────────────────────────────────────────

// Destinations and the door list itself live in gate/gateDoors.ts — the
// match-night rule (window open => exactly two doors) is a product ruling, and
// putting it in a pure module is what lets gateDoors.test.ts assert it. This
// screen is now purely the renderer.

// DEV-only synthetic live window, so match-night mode can be eyeballed with
// ?live=1 (optionally &reason=own|proxy|both) without waiting for a real
// fixture to be inside its T-1h window. Never reachable in a production build.
function previewLiveMatch(params: URLSearchParams): LiveMatchRef {
  const reason = params.get('reason');
  return {
    matchId: 'MATCH#preview',
    compId: 'COMP#preview',
    label: 'Preview Cup · vs Grounded Nation ZA',
    opponentClanName: 'Grounded Nation ZA',
    pendingSlotCount: Number(params.get('pending') ?? 12),
    reason: reason === 'own' || reason === 'both' ? reason : 'proxy',
  };
}

function useGateIdentity(): GateIdentity {
  const [params] = useSearchParams();
  const as = params.get('as');
  const preview = import.meta.env.DEV && as ? (as as GatePersona) : null;
  const livePreview = import.meta.env.DEV && params.get('live') === '1'
    ? previewLiveMatch(params)
    : null;
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, enabled: !preview });

  // The TIME axis, resolved on its own call rather than as a field on /me —
  // /me is the bootstrap every page load makes and this answer is only needed
  // here (see api.myLiveMatch). Independent of `me` loading: a stale or slow
  // window resolve must never hold up the ordinary chooser, so this query has
  // no `me` dependency beyond being skipped entirely under ?as= preview.
  //
  // refetchOnWindowFocus is what makes the gate honest across the T-1h
  // boundary: someone who opened the portal at T-70m and comes back at T-50m
  // gets the window without a reload.
  const { data: live } = useQuery({
    queryKey: ['me', 'live-match'],
    queryFn: api.myLiveMatch,
    enabled: !preview && !livePreview,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  // View-As correctness: a founder previewing a lower platform role (or the
  // "Org admin (scoped)" simulation) must see the GATE the way that persona
  // would — same effective-identity hook every other role-gated surface uses
  // (Sidebar, TournamentSetup, LeaderPanel/MyClanIntro). Distinct from the
  // GATE's own DEV-only ?as= preview above (which fully replaces identity for
  // eyeballing without a live session) — when that's active we skip this
  // entirely since `me` isn't even fetched.
  const effectiveMe = useEffectiveMe(me);
  if (preview) return previewIdentity(preview);
  return resolveGateIdentity(effectiveMe, livePreview ?? live?.liveMatch ?? null);
}

export function GateScreen() {
  const id = useGateIdentity();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<GateScreenId>('root');

  // MATCH-NIGHT MODE. While a submission window is open the door list collapses
  // to submit + dashboard and the sub-screens are unreachable (gateDoors.ts owns
  // that rule and gateDoors.test.ts asserts it) — so the BACK affordance, whose
  // only job is escaping a sub-screen, has nothing to escape.
  const doors = gateDoors(id, screen);
  const header = gateHeader(id, screen);
  const showBack = screen !== 'root' && !id.liveMatch;

  return (
    // No background on the root — body already paints var(--bg-surface); an
    // opaque repaint here would hide the ambient ember canvas behind #root.
    <div className="relative flex min-h-screen flex-col items-center justify-center px-6 py-12">
      {/* Pull the user's Discord accent onto the GATE too (it renders outside
          Layout, so without this it'd default to forge orange). */}
      <AccentInjector />
      {/* key re-mounts the block so the settle entrance replays on each change.
          Items settle top-down (logo → header → pills). Keyed on the live
          window too, so the window OPENING mid-session replays the entrance
          rather than silently swapping the pills under the user's cursor. */}
      <div key={`${screen}:${id.liveMatch?.matchId ?? 'none'}`} className="w-full max-w-[360px]">
        <img
          src="/brand/gganvil-tall.svg"
          alt="GG Anvil"
          className={cn('mx-auto mb-7 h-28 w-auto', SETTLE)}
          style={settleDelay(0)}
        />
        <p
          className={cn('text-center font-display text-sm font-semibold uppercase tracking-[0.32em] text-white/55', SETTLE)}
          style={settleDelay(1)}
        >
          {header}
        </p>

        {/* Which fixture, in plain words. Only on match night — the ordinary
            chooser has no single subject to name. */}
        {id.liveMatch && (
          <p
            className={cn('mt-2 text-center font-sans text-[12px] font-medium text-white/45', SETTLE)}
            style={settleDelay(2)}
          >
            {id.liveMatch.label}
          </p>
        )}

        <div className="mt-7 flex flex-col gap-4">
          {doors.map((door, i) => (
            <GatePill
              key={door.key}
              label={door.label}
              primary={door.primary ?? false}
              {...(door.caption ? { caption: door.caption } : {})}
              idx={i + 2}
              onClick={() => (door.screen ? setScreen(door.screen) : navigate(door.to!))}
            />
          ))}
        </div>
      </div>

      {showBack && (
        <button
          onClick={() => setScreen('root')}
          className={cn('absolute bottom-8 left-8 font-display text-lg font-semibold uppercase tracking-wide text-white/85 hover:text-white', SETTLE, MORPH)}
          style={settleDelay(0)}
        >
          Back
        </button>
      )}
    </div>
  );
}
